import React, { useLayoutEffect, useRef, useState } from "react";

import { Box, ButtonBase } from "@mui/material";

import { focusRing } from "../tokens";

export function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  ariaLabel,
}: {
  options: { value: T; label: string }[];
  value: T;
  onChange: (value: T) => void;
  ariaLabel?: string;
}) {
  const activeIndex = options.findIndex((option) => option.value === value);

  const trackRef = useRef<HTMLDivElement>(null);
  const segmentRefs = useRef<(HTMLButtonElement | null)[]>([]);

  // The thumb's measured geometry, in pixels relative to the track. Segments
  // size to their own content, so the thumb can't be positioned with pure CSS;
  // we measure the active segment and slide the thumb to it.
  const [thumb, setThumb] = useState<{ left: number; width: number }>({
    left: 0,
    width: 0,
  });
  // Stays false until the first measurement lands, so the thumb appears in
  // place on first paint instead of sliding in from the track's left edge.
  const [ready, setReady] = useState(false);

  useLayoutEffect(() => {
    // Drop stale refs if the segment count shrank, so old higher-index
    // entries can't linger and be measured.
    segmentRefs.current.length = options.length;

    const measure = () => {
      const track = trackRef.current;
      const index = options.findIndex((option) => option.value === value);
      const segment = segmentRefs.current[index];
      if (!track || !segment) return;
      const trackRect = track.getBoundingClientRect();
      const segRect = segment.getBoundingClientRect();
      // segRect.left already includes the track's 3px padding offset, so this
      // needs no extra adjustment.
      const left = segRect.left - trackRect.left;
      const width = segRect.width;
      // Bail out of a re-render when the measurement is unchanged.
      setThumb((prev) =>
        prev.left === left && prev.width === width ? prev : { left, width }
      );
      setReady(true);
    };

    measure();

    const track = trackRef.current;
    if (!track) return;
    const observer = new ResizeObserver(() => measure());
    observer.observe(track);
    return () => observer.disconnect();
  }, [value, options.length]);

  return (
    <Box
      ref={trackRef}
      role="radiogroup"
      aria-label={ariaLabel}
      sx={(theme) => ({
        position: "relative",
        display: "flex",
        width: "fit-content",
        padding: "3px",
        borderRadius: "999px",
        backgroundColor: theme.palette.grey[300],
      })}
    >
      {/* A single white thumb that slides under the labels to the active
          segment's measured position and width, rather than each segment
          toggling its own fill. */}
      <Box
        aria-hidden
        sx={(theme) => ({
          position: "absolute",
          top: "3px",
          bottom: "3px",
          left: `${thumb.left}px`,
          width: `${thumb.width}px`,
          opacity: activeIndex < 0 ? 0 : 1,
          backgroundColor: theme.palette.common.white,
          borderRadius: "999px",
          boxShadow: "0 1px 2px rgba(0,0,0,0.12)",
          zIndex: 0,
          pointerEvents: "none",
          transition: ready
            ? theme.transitions.create(["left", "width"], {
                duration: 200,
                easing: theme.transitions.easing.easeInOut,
              })
            : "none",
          "@media (prefers-reduced-motion: reduce)": {
            transition: "none",
          },
        })}
      />
      {options.map((option, index) => {
        const active = option.value === value;
        return (
          <ButtonBase
            key={option.value}
            ref={(el) => {
              segmentRefs.current[index] = el;
            }}
            role="radio"
            aria-checked={active}
            onClick={() => onChange(option.value)}
            disableRipple
            disableTouchRipple
            sx={(theme) => ({
              ...theme.typography.labelSMedium,
              position: "relative",
              zIndex: 1,
              flexShrink: 0,
              whiteSpace: "nowrap",
              height: "28px",
              padding: "0 14px",
              borderRadius: "999px",
              backgroundColor: "transparent",
              transition: theme.transitions.create("color"),
              color: active ? theme.palette.grey[900] : theme.palette.grey[700],
              "&:hover": {
                color: theme.palette.grey[900],
              },
              ...focusRing(theme),
            })}
          >
            {option.label}
          </ButtonBase>
        );
      })}
    </Box>
  );
}
